import React from 'react';
import { Badge } from '@/admin/components/ui/badge';
import { Button } from '@/admin/components/ui/button';
import { Input } from '@/admin/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/admin/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/admin/components/ui/table';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/admin/components/ui/alert-dialog';
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from '@/admin/components/ui/pagination';
import { IconTrash } from '@tabler/icons-react';
import {
  fetchSkillComments,
  deleteSkillComment,
  type SkillComment,
} from '@/admin/lib/api';

export const SkillCommentsPage: React.FC = () => {
  const [comments, setComments] = React.useState<SkillComment[]>([]);
  const [total, setTotal] = React.useState(0);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [currentPage, setCurrentPage] = React.useState(1);
  const [pageSize, setPageSize] = React.useState(20);
  const [skillFilter, setSkillFilter] = React.useState('');
  const [debouncedFilter, setDebouncedFilter] = React.useState('');
  const [deleteTarget, setDeleteTarget] = React.useState<SkillComment | null>(
    null
  );
  const [deleting, setDeleting] = React.useState(false);

  // Debounce skill filter
  React.useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedFilter(skillFilter);
      setCurrentPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [skillFilter]);

  const loadComments = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const offset = (currentPage - 1) * pageSize;
      const result = await fetchSkillComments(
        pageSize,
        offset,
        debouncedFilter || undefined
      );
      setComments(result.comments ?? []);
      setTotal(result.total);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Failed to load comments'
      );
    } finally {
      setLoading(false);
    }
  }, [currentPage, pageSize, debouncedFilter]);

  React.useEffect(() => {
    loadComments();
  }, [loadComments]);

  const totalPages = Math.ceil(total / pageSize);
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = Math.min(startIndex + pageSize, total);

  const handlePageChange = (page: number) => {
    setCurrentPage(Math.max(1, Math.min(page, totalPages)));
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await deleteSkillComment(deleteTarget.id);
      setDeleteTarget(null);
      loadComments();
    } catch {
      // Error handled silently, list will refresh
    } finally {
      setDeleting(false);
    }
  };

  const formatDateTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });
  };

  const scoreBadge = (score: number) => {
    const variant =
      score <= 1 ? 'destructive' : score <= 2 ? 'outline' : 'secondary';
    return (
      <Badge variant={variant} className="text-xs">
        {score}
      </Badge>
    );
  };

  return (
    <div className="flex h-full flex-col">
      {/* Header */}
      <div className="shrink-0 bg-background px-4 py-4 lg:px-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex flex-wrap items-center gap-3">
            <span className="text-sm font-medium text-muted-foreground">
              Total: {total} comments
            </span>
            <Input
              placeholder="Filter by skill name..."
              value={skillFilter}
              onChange={e => setSkillFilter(e.target.value)}
              className="h-8 w-[200px]"
            />
          </div>

          {!loading && total > 0 && (
            <div className="flex items-center gap-3">
              <span className="text-sm text-muted-foreground">
                {startIndex + 1}-{endIndex} of {total}
              </span>
              <Select
                value={pageSize.toString()}
                onValueChange={value => {
                  setPageSize(Number(value));
                  setCurrentPage(1);
                }}
              >
                <SelectTrigger className="h-8 w-[70px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="10">10</SelectItem>
                  <SelectItem value="20">20</SelectItem>
                  <SelectItem value="50">50</SelectItem>
                </SelectContent>
              </Select>

              <Pagination className="w-auto">
                <PaginationContent>
                  <PaginationItem>
                    <PaginationPrevious
                      onClick={() => handlePageChange(currentPage - 1)}
                      className={
                        currentPage === 1
                          ? 'pointer-events-none opacity-50'
                          : 'cursor-pointer'
                      }
                    />
                  </PaginationItem>

                  {Array.from({ length: totalPages }).map((_, i) => {
                    const page = i + 1;
                    const showPage =
                      page === 1 ||
                      page === totalPages ||
                      Math.abs(page - currentPage) <= 1;

                    if (!showPage && totalPages > 5) {
                      const isLeftGap =
                        page === currentPage - 2 && currentPage > 3;
                      const isRightGap =
                        page === currentPage + 2 &&
                        currentPage < totalPages - 2;
                      if (isLeftGap || isRightGap) {
                        return (
                          <PaginationItem key={page}>
                            <PaginationEllipsis />
                          </PaginationItem>
                        );
                      }
                      return null;
                    }

                    return (
                      <PaginationItem key={page}>
                        <PaginationLink
                          isActive={page === currentPage}
                          onClick={() => handlePageChange(page)}
                          className="cursor-pointer"
                        >
                          {page}
                        </PaginationLink>
                      </PaginationItem>
                    );
                  })}

                  <PaginationItem>
                    <PaginationNext
                      onClick={() => handlePageChange(currentPage + 1)}
                      className={
                        currentPage === totalPages
                          ? 'pointer-events-none opacity-50'
                          : 'cursor-pointer'
                      }
                    />
                  </PaginationItem>
                </PaginationContent>
              </Pagination>
            </div>
          )}
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="mx-4 mb-4 rounded-md bg-destructive/10 p-3 text-sm text-destructive lg:mx-6">
          {error}
        </div>
      )}

      {/* Table */}
      <div className="min-h-0 flex-1 overflow-auto px-4 lg:px-6">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[50px]">Score</TableHead>
              <TableHead className="w-[150px]">Skill</TableHead>
              <TableHead className="w-[80px]">Source</TableHead>
              <TableHead className="w-[120px]">User</TableHead>
              <TableHead>Comment</TableHead>
              <TableHead className="w-[120px]">Time</TableHead>
              <TableHead className="w-[60px]" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={7} className="text-center py-8">
                  Loading...
                </TableCell>
              </TableRow>
            ) : comments.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={7}
                  className="text-center py-8 text-muted-foreground"
                >
                  No comments found
                </TableCell>
              </TableRow>
            ) : (
              comments.map(c => (
                <TableRow key={c.id}>
                  <TableCell>{scoreBadge(c.score)}</TableCell>
                  <TableCell className="font-mono text-xs">
                    {c.skill_name}
                    {c.repo && (
                      <div className="text-muted-foreground">{c.repo}</div>
                    )}
                  </TableCell>
                  <TableCell>
                    <Badge
                      variant={c.source === 'external' ? 'outline' : 'secondary'}
                      className="text-xs"
                    >
                      {c.source}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-sm">
                    {c.user_display_name || '—'}
                  </TableCell>
                  <TableCell className="text-sm max-w-[300px] truncate">
                    {c.comment}
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground">
                    {formatDateTime(c.created_at)}
                  </TableCell>
                  <TableCell>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 text-muted-foreground hover:text-destructive"
                      onClick={() => setDeleteTarget(c)}
                    >
                      <IconTrash className="h-4 w-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* Delete Confirmation */}
      <AlertDialog
        open={!!deleteTarget}
        onOpenChange={open => !open && setDeleteTarget(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete comment?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete the comment from{' '}
              <span className="font-mono">{deleteTarget?.skill_name}</span>.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={deleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleting ? 'Deleting...' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
